import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateTaskInput, Task, TaskStats, UpdateTaskInput } from '@guabi/shared';
import { api, apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api';

export const tasksQueryKey = (wedId: string) => ['tasks', wedId] as const;

export interface TasksResponse {
  items: Task[];
  stats: TaskStats;
  weddingDate?: string;
  /**
   * How many generated tasks no longer match the wedding date. Computed on the
   * server, which is the only side that holds the template's anchors.
   */
  staleCount: number;
}

export function useTasks(wedId: string) {
  return useQuery({
    queryKey: tasksQueryKey(wedId),
    queryFn: () => apiGet<TasksResponse>(`/weddings/${wedId}/tasks`),
  });
}

function useTaskMutation<TArgs, TResult = void>(
  fn: (args: TArgs) => Promise<TResult>,
  wedId: string,
) {
  const qc = useQueryClient();
  return useMutation<TResult, Error, TArgs>({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tasksQueryKey(wedId) });
      void qc.invalidateQueries({ queryKey: ['summary', wedId] });
    },
  });
}

export function useCreateTask(wedId: string) {
  return useTaskMutation<CreateTaskInput, Task>(
    (input) => apiPost<Task>(`/weddings/${wedId}/tasks`, input),
    wedId,
  );
}

export function useUpdateTask(wedId: string) {
  return useTaskMutation<{ taskId: string; input: UpdateTaskInput & { updatedAt: string } }, Task>(
    ({ taskId, input }) => apiPatch<Task>(`/weddings/${wedId}/tasks/${taskId}`, input),
    wedId,
  );
}

export function useDeleteTask(wedId: string) {
  return useTaskMutation<{ taskId: string }>(
    ({ taskId }) => apiDelete(`/weddings/${wedId}/tasks/${taskId}`),
    wedId,
  );
}

export function useGenerateChecklist(wedId: string) {
  return useTaskMutation<void, { created: number }>(
    () => apiPost<{ created: number }>(`/weddings/${wedId}/tasks/generate`, {}),
    wedId,
  );
}

export function useRescheduleChecklist(wedId: string) {
  return useTaskMutation<void, { moved: number }>(
    () => apiPost<{ moved: number }>(`/weddings/${wedId}/tasks/reschedule`, {}),
    wedId,
  );
}

export interface PlanSuggestion {
  title: string;
  why: string;
  category: string;
  dueDate?: string;
}

/**
 * What a planner would say to do next. Writes nothing — each suggestion becomes
 * a task only when the couple presses add, the same shape as the guest import
 * and the seating pass.
 */
export function useSuggestions(wedId: string) {
  const qc = useQueryClient();
  return useMutation<{ suggestions: PlanSuggestion[]; note?: string }, Error, void>({
    mutationFn: () =>
      api<{ suggestions: PlanSuggestion[]; note?: string }>(`/weddings/${wedId}/ai/suggestions`, {
        method: 'POST',
        body: JSON.stringify({ locale: navigator.language }),
        timeoutMs: 28_000,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ai-usage', wedId] }),
  });
}
